// Copyright 2022, Imprivata, Inc.  All rights reserved.

import { Button, HexagonSpinner } from '@imprivata-cloud/patient-access-ui-components';
import { Flex, Typography } from 'antd';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetPatientConsentConfigQuery } from '../../api/hooks/getPatientConsentConfigQuery';
import { ErrorComponent } from '../error/ErrorComponent';
import './PatientConsentStep.less';

interface PatientConsentStepProps {
  onContinue: () => void;
}

export const PatientConsentStep = ({ onContinue }: PatientConsentStepProps) => {
  const { t } = useTranslation();
  const { config, isLoading, isError, setEnabled } = useGetPatientConsentConfigQuery();

  useEffect(() => {
    setEnabled();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (config && !config.documentUrl) {
      onContinue();
    }
  }, [config, onContinue]);

  if (isError) {
    return <ErrorComponent cause="unknown" />;
  }

  if (isLoading || !config) {
    return <HexagonSpinner data-testid="loading-spinner" />;
  }

  if (!config.documentUrl) {
    return null;
  }

  return (
    <Flex vertical align="center" className="w-100" gap={16} data-testid="patient-consent-step">
      <Typography.Title level={3} className="text-center">
        {t('patientConsentPage.title')}
      </Typography.Title>
      <Typography.Text className="text-center">
        {t('patientConsentPage.description')}
      </Typography.Text>
      <div className="patient-consent-step__iframe-container">
        <iframe
          src={config.documentUrl}
          sandbox="allow-popups"
          title={t('patientConsentPage.iframeLabel')}
          data-testid="consent-iframe"
          className="patient-consent-step__iframe"
        />
      </div>
      <Button
        variant="primary"
        onClick={onContinue}
        label={t('patientConsentPage.acceptButton')}
        className="w-100"
        data-testid="accept-button"
      />
      <Button
        variant="secondary"
        label={t('patientConsentPage.declineButton')}
        className="w-100"
        data-testid="decline-button"
      />
    </Flex>
  );
};
